import React, { useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { ProductRow } from "models/Order";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import StyledTable from "components/StyledTable";

type Props = {
  rows: ProductRow[];
  onSelect?: (product: ProductRow) => void;
};

type RowWithAvailable = ProductRow & { available: number };

type SortBy = "rowId" | "item" | "description" | "serialRequired" | "available";

const sortRows = (rows: RowWithAvailable[], sortBy: SortBy, sortDir: "asc" | "desc") => {
  return [...rows].sort((a, b) => {
    let aValue: number | string | boolean = a[sortBy];
    let bValue: number | string | boolean = b[sortBy];

    // Handle undefined values
    if (aValue === undefined) aValue = sortBy === "available" ? 0 : "";
    if (bValue === undefined) bValue = sortBy === "available" ? 0 : "";

    if (aValue < bValue) return sortDir === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
};

const ProductRowsTable: React.FC<Props> = ({ rows, onSelect }) => {
  const [sortBy, setSortBy] = useState<SortBy>("rowId");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const updateSortBy = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const rowsWithAvailable: RowWithAvailable[] = useMemo(() => {
    return rows.map((row) => {
      const numDelivered = row.quantityDelivered ?? 0;
      const numInstalled = row.serials.filter((serial) => serial.installed).length;
      let numAvailable = 0;
      if (row.serialRequired || row.serials.length > 0)
        numAvailable = Math.max(numDelivered - numInstalled, 0);
      return { ...row, available: numAvailable };
    });
  }, [rows]);

  const rowsGroupedByOrder = useMemo(() => {
    const grouped: { [orderNumber: string]: RowWithAvailable[] } = {};
    rowsWithAvailable.forEach((row) => {
      if (!grouped[row.orderNumber]) {
        grouped[row.orderNumber] = [];
      }
      grouped[row.orderNumber].push(row);
    });
    Object.keys(grouped).forEach((orderNumber) => {
      grouped[orderNumber] = sortRows(grouped[orderNumber], sortBy, sortDir);
    });
    return grouped;
  }, [rowsWithAvailable, sortBy, sortDir]);

  return (
    <TableContainer>
      <StyledTable size="small" striped noBottomBorder>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === "rowId"}
                direction={sortDir}
                onClick={() => updateSortBy("rowId")}
              >
                Orderrad
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "item"}
                direction={sortDir}
                onClick={() => updateSortBy("item")}
              >
                Artikelkod
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "description"}
                direction={sortDir}
                onClick={() => updateSortBy("description")}
              >
                Benämning
              </TableSortLabel>
            </TableCell>

            <TableCell align="right">
              <TableSortLabel
                active={sortBy === "serialRequired"}
                direction={sortDir}
                onClick={() => updateSortBy("serialRequired")}
              >
                Serienummerbelagd
              </TableSortLabel>
            </TableCell>

            <TableCell align="right">
              <TableSortLabel
                active={sortBy === "available"}
                direction={sortDir}
                onClick={() => updateSortBy("available")}
              >
                Tillgängliga
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rowsWithAvailable.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ fontStyle: "italic" }}>
                Ordern innehåller inga artiklar
              </TableCell>
            </TableRow>
          )}

          {/* Group rows by order number */}
          {Object.entries(rowsGroupedByOrder).map(([orderNumber, rows]) => (
            <React.Fragment key={orderNumber}>
              {/* Order number subheader */}
              <TableRow>
                <TableCell align="center" colSpan={100} sx={{ fontWeight: "bold" }}>
                  {orderNumber}
                </TableCell>
              </TableRow>

              {rows.map((row) => (
                <TableRow
                  key={`${row.orderNumber}:${row.rowId}`}
                  hover={true}
                  onClick={() => onSelect?.(row)}
                >
                  <TableCell>{row.rowId}</TableCell>
                  <TableCell sx={{ textWrap: "nowrap" }}>{row.item}</TableCell>

                  <TableCell>{row.description}</TableCell>

                  <TableCell align="right">{row.serialRequired && <CheckIcon />}</TableCell>

                  <TableCell align="right">{row.available ? row.available : null}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default ProductRowsTable;
